import { c3dSkinRamp, type CharacterAppearance } from "@ghostland/shared";
import { assetUrl } from "../assetVer.js";

/**
 * Renderer-agnostic c3d helpers (no Phaser). Shared by the Phaser rig and the
 * Three.js billboard so the 3D world can be built without bundling Phaser.
 *
 * Also environment-agnostic: the compositor runs inside a Web Worker (see c3dPipeline)
 * where there is no DOM — canvases become OffscreenCanvas and sheets load via
 * fetch + createImageBitmap instead of <img>.
 */

/** True when running inside a Web Worker (no DOM). */
const IS_WORKER = typeof document === "undefined";

/** A part sheet as loaded for compositing: an <img> on the main thread, an ImageBitmap in a worker. */
export type SheetImage = HTMLImageElement | ImageBitmap;

/** A 2D canvas that works on both the main thread and inside a worker. Typed as HTMLCanvasElement
 *  for call-site simplicity — in a worker it's really an OffscreenCanvas with the same 2D API. */
export function newCanvas(w: number, h: number): HTMLCanvasElement {
  if (IS_WORKER) return new OffscreenCanvas(w, h) as unknown as HTMLCanvasElement;
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

const imgCache = new Map<string, Promise<SheetImage | null>>();

/** Load (and cache) a sprite-sheet image; resolves null on error rather than throwing. */
export function loadPartImage(path: string): Promise<SheetImage | null> {
  let p = imgCache.get(path);
  if (!p) {
    // ?v=<build> busts stale CDN/browser cache after a re-render
    p = IS_WORKER
      ? fetch(assetUrl(path))
          .then((r) => (r.ok ? r.blob().then((b) => createImageBitmap(b)) : null))
          .catch(() => null)
      : new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => resolve(null);
          img.src = assetUrl(path);
        });
    imgCache.set(path, p);
  }
  return p;
}

/**
 * Recolour the c3d body's SKIN pixels onto the chosen tone's [shadow, mid, light]
 * ramp, preserving shading. Skin = warm pixels (red-dominant, meaningfully warm); the
 * white/grey clothes and the dark desaturated eyes don't match, so they're untouched.
 * Tone 0 (ramp null) returns the image unchanged. Luminance is normalised to the
 * skin's typical range so the remap keeps full step contrast.
 */
export function recolorBodySkin(
  src: SheetImage,
  skinTone: number,
): SheetImage | HTMLCanvasElement {
  const ramp = c3dSkinRamp(skinTone);
  if (!ramp) return src;
  const canvas = newCanvas(src.width, src.height);
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(src, 0, 0);
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const [lo, mid, hi] = ramp;
  const LO = 0.22, HI = 0.9; // skin luminance range to normalise across
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] < 128) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    let L = (0.3 * r + 0.59 * g + 0.11 * b) / 255;
    // warm, red-dominant skin only. Exclude near-white clothes (low r-b) AND the EYES
    // + dark outline: eyes are warm-but-DARK (L≈0.25), real skin (even shadow) is L>0.34,
    // so the luminance floor keeps the eye colour fixed across skin tones.
    if (!(r > g && g >= b && r - b > 24 && L > 0.34)) continue;
    L = Math.min(1, Math.max(0, (L - LO) / (HI - LO)));
    let c: readonly [number, number, number];
    if (L < 0.5) {
      const t = L / 0.5;
      c = [lo[0] + (mid[0] - lo[0]) * t, lo[1] + (mid[1] - lo[1]) * t, lo[2] + (mid[2] - lo[2]) * t];
    } else {
      const t = (L - 0.5) / 0.5;
      c = [mid[0] + (hi[0] - mid[0]) * t, mid[1] + (hi[1] - mid[1]) * t, mid[2] + (hi[2] - mid[2]) * t];
    }
    d[i] = c[0]; d[i + 1] = c[1]; d[i + 2] = c[2];
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/** Plain `CharacterAppearance` snapshot from a synced schema instance. */
export function appearanceFromSchema(s: CharacterAppearance): CharacterAppearance {
  return {
    head: s.head,
    face: s.face,
    skinTone: s.skinTone,
    hair: s.hair,
    hairColor: s.hairColor,
    backHair: s.backHair,
    beard: s.beard,
    beardColor: s.beardColor,
    ears: s.ears,
    earsColor: s.earsColor,
    glasses: s.glasses,
    glassesColor: s.glassesColor,
    backAcc: s.backAcc,
    backAccColor: s.backAccColor,
    top: s.top,
    topColor: s.topColor,
    bottom: s.bottom,
    bottomColor: s.bottomColor,
    shoes: s.shoes,
    shoesColor: s.shoesColor,
    hat: s.hat,
    hatColor: s.hatColor,
    mask: s.mask,
    maskColor: s.maskColor,
    weapon: s.weapon,
  };
}
